/**
 * EpilepsyWarning — Photosensitive Seizure Notice
 * 
 * Informs users that the site contains animated visual effects including
 * color gradients, pulsing lights, and moving geometric patterns.
 * All animations operate below the WCAG 2.3.1 threshold of 3 Hz.
 * 
 * WCAG 2.3.1 Reference: Web content should not contain anything that
 * flashes more than 3 times per second.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const STORAGE_KEY = 'fr00t-epilepsy-warning-acknowledged';

export function hasAcknowledgedWarning(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

const EpilepsyWarning: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show warning unless user has already acknowledged
    if (!hasAcknowledgedWarning()) {
      const timer = setTimeout(() => setVisible(true), 300);
      return () => clearTimeout(timer);
    }
  }, []);

  const acknowledge = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch { /* localStorage unavailable */ }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            background: 'rgba(9, 9, 11, 0.97)',
            backdropFilter: 'blur(16px)',
            borderTop: '1px solid rgba(255, 215, 0, 0.3)',
            padding: '14px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            flexWrap: 'wrap',
            fontFamily: 'system-ui, sans-serif',
          }}
          role="alert"
          aria-live="polite"
        >
          <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
          <span style={{
            fontSize: 13,
            color: '#d4d4d8',
            lineHeight: 1.5,
            maxWidth: 600,
            textAlign: 'center',
          }}>
            <strong style={{ color: '#facc15' }}>Photosensitive Epilepsy Notice:</strong>
            {' '}This site contains animated visual effects including color gradients,
            pulsing lights, holographic patterns, and moving geometric shapes.
            All animations operate below the WCAG 2.3.1 safety threshold of 3 Hz.
            If you are sensitive to visual motion, please proceed with caution.
          </span>
          <button
            onClick={acknowledge}
            style={{
              background: 'rgba(250, 204, 21, 0.15)',
              color: '#facc15',
              border: '1px solid rgba(250, 204, 21, 0.3)',
              borderRadius: 6,
              padding: '8px 20px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(250, 204, 21, 0.25)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(250, 204, 21, 0.15)'; }}
          >
            I Understand
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default EpilepsyWarning;
